/**
 * Normalisation for RFC 5322 message identifiers, matching MessageReferenceParser in the webapp repo.
 *
 * The two copies must agree exactly. Every capture surface feeds the same two columns, and the thread
 * resolver joins one against the other on equality - so if this normalised differently from the
 * server, interactions saved from Outlook would simply never match anything, silently and only for
 * Outlook. Change these rules in both places or in neither.
 */

/**
 * Matches the column width on both sides of the join. 450 is the largest indexable nvarchar key on
 * SQL Server; a longer id is malformed, and truncating it would produce a value that matches the
 * wrong message rather than none.
 */
export const MAXIMUM_MESSAGE_ID_LENGTH = 450;

/**
 * Upper bound on how much of a References chain is kept. Real chains run to twenty or so; this exists
 * so a malformed header cannot send unbounded rows. Truncation keeps the newest entries, which are
 * the nearest ancestors and the likeliest to have been logged.
 */
export const MAXIMUM_REFERENCE_COUNT = 50;

/**
 * Reduces a single identifier to its stored form: angle brackets stripped, whitespace trimmed.
 * Returns null when there is nothing usable, which callers treat as "no id" rather than sending an
 * empty string.
 *
 * The brackets are the point: Graph's `internetMessageId` includes them and a raw header usually
 * does, but not every client agrees - and storing them inconsistently would break the join for
 * exactly the messages that crossed platforms.
 */
export function normaliseMessageId(value: string | null | undefined): string | null {
  if (!value) return null;

  const trimmed = value.trim().replace(/^<+/, '').replace(/>+$/, '').trim();

  if (trimmed.length === 0 || trimmed.length > MAXIMUM_MESSAGE_ID_LENGTH) {
    return null;
  }

  return trimmed;
}

/**
 * Splits a raw References or In-Reply-To header into its identifiers.
 *
 * The header is a whitespace-separated list of angle-bracketed ids, oldest first, but real mail folds
 * it across lines and some clients separate with commas.
 */
export function parseReferenceHeader(header: string | null | undefined): string[] {
  if (!header) return [];

  return normaliseMessageIds(header.split(/[\s,]+/));
}

/**
 * Normalises a list of identifiers, dropping the unusable, removing duplicates keeping first
 * occurrence, and applying {@link MAXIMUM_REFERENCE_COUNT}.
 */
export function normaliseMessageIds(messageIds: readonly string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const messageId of messageIds) {
    const normalised = normaliseMessageId(messageId);

    if (normalised === null || seen.has(normalised)) continue;

    seen.add(normalised);
    result.push(normalised);
  }

  // Keep the tail: References is ordered oldest first, so the newest entries are the nearest
  // ancestors and the likeliest to match something that was actually logged.
  return result.length <= MAXIMUM_REFERENCE_COUNT
    ? result
    : result.slice(result.length - MAXIMUM_REFERENCE_COUNT);
}

/**
 * Builds the chain to send: References, with In-Reply-To appended when it names an ancestor
 * References did not.
 *
 * Both are read because they disagree in practice - some clients send In-Reply-To without References
 * at all, and a chain of one still threads a reply onto its parent.
 */
export function buildReferenceChain(
  referencesHeader: string | null | undefined,
  inReplyToHeader: string | null | undefined
): string[] {
  const references = parseReferenceHeader(referencesHeader);
  const inReplyTo = parseReferenceHeader(inReplyToHeader);

  return inReplyTo.length === 0
    ? references
    : normaliseMessageIds([...references, ...inReplyTo]);
}
